import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Typography,
  TablePagination,
  IconButton,
  Grid,
  useMediaQuery
} from "@material-ui/core";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { createTheme } from "@material-ui/core/styles";
import MainContainer from '../../../components/MainContainer';
import { headerData } from '../../../data/modules/my-validations-header';
import FileValidationRequestService from "../../../services/FileValidationRequestService";
import { 
  FindInPageTwoTone,
  FilterList,
  Clear,
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  BeachAccess,
  Flight,
  Description,
  Apps,
  Visibility
} from "@material-ui/icons";
import RequestSummary from "../../tools/RequestSummary";
import RequestSummaryMission from "../../tool-accepted/RequestSummary";
import { useNavigate } from "react-router-dom";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import { hasPrivilege } from "../../../utils/privilege";
import { userDetailContext } from "../../../App";
import { useContext } from "react";
import moment from "moment";

const defaultTheme = createTheme();

// Les 3 seuls types de demandes gérés par ce module.
const normalizeType = (str) =>
  (str || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const REQUEST_TYPES = [
  {
    key: "conge",
    label: "Congé",
    icon: BeachAccess,
    match: (raw) => normalizeType(raw) === "conge",
  },
  {
    key: "mission",
    label: "Mission",
    icon: Flight,
    match: (raw) => normalizeType(raw) === "mission",
  },
  {
    key: "rapport de mission",
    label: "Rapport de mission",
    icon: Description,
    match: (raw) => normalizeType(raw) === "rapport de mission",
  },
];

const STATS_DISPLAY = [
  { key: "all", label: "Total", icon: Apps, match: () => true },
  ...REQUEST_TYPES,
];

// Composant pour les actions de pagination personnalisées
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: { xs: 0.5, sm: 2.5 } }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="première page"
        size="small"
      >
        {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="page précédente"
        size="small"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="page suivante"
        size="small"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="dernière page"
        size="small"
      >
        {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      width: '100%',
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      },
      '& .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
      },
      '& .row-old': {
        backgroundColor: '#f0b4b4 !important',
      },
      '& .row-old:hover': {
        backgroundColor: '#e26c6c !important',
      },
    },
    mainContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    paper: {
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      borderRadius: theme.spacing(1),
      flex: 1,
      overflow: 'hidden',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.75),
        borderRadius: 0,
        height: 'calc(100vh - 100px)',
        minHeight: '450px',
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
      },
    },
    container: {
      width: '100%',
      margin: 0,
      padding: 0,
      height: '100%',
    },
    filterContainer: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[1],
      flexShrink: 0,
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.75),
        borderRadius: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
      },
    },
    statsContainer: {
      display: 'flex',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      flexWrap: 'wrap',
      justifyContent: 'center',
      flexShrink: 0,
      [theme.breakpoints.down('xs')]: {
        gap: theme.spacing(0.5),
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
      },
    },
    statCard: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
      borderRadius: theme.spacing(1),
      cursor: 'pointer',
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.15s ease-in-out',
      minWidth: '140px',
      flex: '1 1 140px',
      '&:hover': {
        boxShadow: theme.shadows[3],
        transform: 'translateY(-2px)',
      },
      [theme.breakpoints.down('xs')]: {
        minWidth: 'calc(25% - 4px)',
        flex: '1 1 calc(25% - 4px)',
        padding: theme.spacing(0.5, 0.25),
        gap: theme.spacing(0.25),
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        borderRadius: theme.spacing(0.25),
      },
      [theme.breakpoints.down(400)]: {
        minWidth: 'calc(33.33% - 4px)',
        flex: '1 1 calc(33.33% - 4px)',
        padding: theme.spacing(0.25, 0.1),
      },
    },
    statCardActive: {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      '& $statCardLabel, & $statCardCount': {
        color: theme.palette.common.white,
      },
      '& svg': {
        color: theme.palette.common.white,
      },
    },
    statCardIcon: {
      fontSize: 24,
      color: theme.palette.primary.main,
      [theme.breakpoints.down('xs')]: {
        fontSize: 18,
      },
    },
    statCardLabel: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.2,
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.65rem',
      },
    },
    statCardCount: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      lineHeight: 1.2,
      [theme.breakpoints.down('xs')]: {
        fontSize: '1rem',
      },
    },
    tableContainer: {
      flex: 1,
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[2],
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      minHeight: 0,
      [theme.breakpoints.down('xs')]: {
        borderRadius: theme.spacing(0.5),
        maxHeight: 'none',
      },
    },
    tableContainerDesktop: {
      maxHeight: '55vh',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
    statusChip: {
      marginRight: theme.spacing(0.5),
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.6rem',
        height: '20px',
      },
    },
    actionButton: {
      minWidth: 'auto',
      padding: theme.spacing(0.5),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.25),
        '& .MuiButton-label': {
          fontSize: '0.7rem',
        },
      },
    },
    filterButton: {
      height: '40px',
      whiteSpace: 'nowrap',
      padding: '0 16px',
      [theme.breakpoints.down('xs')]: {
        height: '34px',
        fontSize: '0.7rem',
        minHeight: '34px',
        padding: '0 8px',
        whiteSpace: 'nowrap',
      },
    },
    typeFilter: {
      width: '100%',
      '& .MuiInputBase-root': {
        height: '40px',
        [theme.breakpoints.down('xs')]: {
          height: '34px',
          fontSize: '0.75rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
        [theme.breakpoints.down('xs')]: {
          fontSize: '0.75rem',
        },
      },
    },
    pagination: {
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      flexShrink: 0,
      [theme.breakpoints.down('xs')]: {
        '& .MuiTablePagination-toolbar': {
          flexWrap: 'wrap',
          justifyContent: 'center',
          padding: theme.spacing(0.25),
          minHeight: '36px',
        },
        '& .MuiTablePagination-select': {
          fontSize: '0.7rem',
          padding: '2px 20px 2px 8px',
        },
        '& .MuiTablePagination-selectLabel': {
          fontSize: '0.7rem',
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          fontSize: '0.7rem',
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          marginLeft: '4px',
        },
      },
    },
    resultsInfo: {
      padding: theme.spacing(0.5, 1),
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.spacing(0.5),
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.25),
      },
    },
    legendRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center',
        gap: theme.spacing(0.5),
      },
    },
    footerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 16px',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      flexShrink: 0,
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center',
        textAlign: 'center',
        flexDirection: 'row',
        padding: theme.spacing(0.25),
        gap: theme.spacing(0.25),
        flexWrap: 'wrap',
      },
    },
    oldRowIndicator: {
      backgroundColor: '#f0b4b4',
      marginRight: theme.spacing(0.5),
      border: '1px solid #ccc',
      borderRadius: '2px',
      display: 'inline-block',
      [theme.breakpoints.down('xs')]: {
        width: '12px',
        height: '12px',
        marginRight: theme.spacing(0.25),
      },
    },
    newRowIndicator: {
      backgroundColor: '#ffffff',
      border: '1px solid #ccc',
      borderRadius: '2px',
      display: 'inline-block',
      [theme.breakpoints.down('xs')]: {
        width: '12px',
        height: '12px',
      },
    },
    gridItem: {
      width: '100%',
      padding: 0,
      height: '100%',
    },
    mobileCardContainer: {
      flex: 1,
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      padding: theme.spacing(0.25),
    },
    mobileCard: {
      padding: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      '&:last-child': {
        marginBottom: 0,
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.75),
      },
    },
    mobileCardOld: {
      backgroundColor: '#f0b4b4',
    },
    mobileCardRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0.25, 0),
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    mobileCardLabel: {
      fontWeight: 'bold',
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      minWidth: '80px',
      [theme.breakpoints.down(400)]: {
        minWidth: '60px',
        fontSize: '0.7rem',
      },
    },
    mobileCardValue: {
      fontSize: '0.75rem',
      textAlign: 'right',
      wordBreak: 'break-word',
      flex: 1,
      [theme.breakpoints.down(400)]: {
        fontSize: '0.7rem',
      },
    },
    mobileCardChip: {
      fontSize: '0.6rem',
      height: '20px',
    },
    mobileCardActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      gap: theme.spacing(0.5),
      flexWrap: 'wrap',
    },
    mobileInfoText: {
      fontSize: '0.65rem',
      color: theme.palette.text.secondary,
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.6rem',
      },
    },
    mobilePaper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
      borderRadius: theme.spacing(0.25),
    },
    emptyState: {
      padding: theme.spacing(2),
      textAlign: 'center',
    },
    mobileActionButton: {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.7rem',
      minHeight: '28px',
      minWidth: 'auto',
    },
    buttonGridItem: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        padding: '4px !important',
      },
    },
    buttonGridContainer: {
      width: '100%',
      margin: 0,
      [theme.breakpoints.down('xs')]: {
        margin: 0,
        width: '100%',
      },
    },
    buttonWrapper: {
      display: 'flex',
      gap: theme.spacing(1),
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        gap: theme.spacing(0.5),
      },
    },
    filterGridItem: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        padding: '4px !important',
      },
    },
    legendGridItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center',
        padding: '4px !important',
      },
    },
  }),
  { defaultTheme },
);

export default function ValidationList() {
  const navigate = useNavigate();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmallMobile = useMediaQuery('(max-width:400px)');
  const contextData = useContext(userDetailContext);
  const listRef = useRef(null);
  
  // États
  const [typeFilter, setTypeFilter] = useState("all");
  const [openDialogRequestSummary, setOpenDialogRequestSummary] = useState(false);
  const [openDialogRequestSummaryMission, setOpenDialogRequestSummaryMission] = useState(false);
  const [requestSummaryId, setRequestSummaryId] = useState({});
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [isRhConnected, setIsRhConnected] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 1 : 10);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  // Ajuster rowsPerPage sur mobile
  useEffect(() => {
    if (isMobile) {
      setRowsPerPage(1);
    } else {
      setRowsPerPage(10);
    }
  }, [isMobile]);

  // Fonction pour parser une date
  const parseDateString = (dateString) => {
    if (!dateString) return new Date(0);
    
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
    }
    
    try {
      const momentDate = moment(dateString);
      if (momentDate.isValid()) {
        return momentDate.toDate();
      }
    } catch (error) {
      console.warn("Erreur avec moment.js:", error);
    }
    
    try {
      const jsDate = new Date(dateString);
      if (!isNaN(jsDate.getTime())) {
        return jsDate;
      }
    } catch (error) {
      console.warn("Erreur avec Date JavaScript:", error);
    }
    
    return new Date(0);
  };

  // Fonction pour formater une date en DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = parseDateString(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Erreur lors du formatage de la date:", error);
      return dateString;
    }
  };

  // Fonction pour trier les données par date de création décroissante
  const sortBySubmissionDateDesc = (data) => {
    return [...data].sort((a, b) => {
      try {
        const dateA = parseDateString(a.submission_time);
        const dateB = parseDateString(b.submission_time);
        return dateB - dateA;
      } catch (error) {
        console.error("Erreur lors du tri des dates:", error);
        return 0;
      }
    });
  };

  // Chargement des données
  useEffect(() => {
    setOpenLoadingSimpleBackdrop(true);
    FileValidationRequestService.getMyFileValidations().then((result) => {
      if (result && result.data) {
        const formattedRows = result.data.map((row) => ({
          ...row,
          id: row.file_validation_request_id,
          formatted_submission_date: formatDateToDDMMYYYY(row.submission_time),
          parsed_submission_date: parseDateString(row.submission_time)
        }));
        
        const sortedRows = sortBySubmissionDateDesc(formattedRows);
        setRows(sortedRows);
        setFilteredRows(sortedRows);
        setOpenLoadingSimpleBackdrop(false);
      } else {
        setOpenLoadingSimpleBackdrop(false);
      }
    });
  }, []);

  // Réinitialiser la page à 0 quand les données filtrées changent
  useEffect(() => {
    setPage(0);
  }, [filteredRows]);

  // Vérifier les privilèges RH
  useEffect(() => {
    if (hasPrivilege(contextData.privileges, 'RH_list_of_request')) {
      setIsRhConnected(true);
    }
  }, [contextData]);

  // Fonction pour vérifier si une date dépasse 1 semaine
  const isOlderThanTwoWeeks = (dateString) => {
    if (!dateString) return false;
  
    try {
      const submissionDate = parseDateString(dateString);
      const today = new Date();
      const diffInMs = today - submissionDate;
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
      return diffInDays > 7;
    } catch (error) {
      console.error("Erreur lors de la vérification de la date:", error);
      return false;
    }
  };

  // Compte le nombre de lignes correspondant à un type donné
  const getCountForType = (typeDef) => {
    return rows.filter((row) => typeDef.match(row.libelle_module_type)).length;
  };

  // Appliquer les filtres
  const applyFilters = (currentTypeFilter = typeFilter) => {
    let filtered = [...rows];

    if (currentTypeFilter !== "all") {
      const typeDef = REQUEST_TYPES.find((t) => t.key === currentTypeFilter);
      if (typeDef) {
        filtered = filtered.filter((row) => typeDef.match(row.libelle_module_type));
      }
    }

    filtered = sortBySubmissionDateDesc(filtered);
    setFilteredRows(filtered);
  };

  useEffect(() => {
    applyFilters(typeFilter);
  }, [typeFilter, rows]);

  // Réinitialiser tous les filtres
  const resetFilters = () => {
    setTypeFilter("all");
    setFilteredRows(sortBySubmissionDateDesc(rows));
  };

  // Gestion des clics sur les statistiques
  const handleStatClick = (typeKey) => () => {
    if (typeKey === "all") {
      setTypeFilter("all");
      return;
    }
    setTypeFilter((prev) => (prev === typeKey ? "all" : typeKey));
  };

  // Formater la date de début/fin
  const formatDate = (dateString, session) => {
    if (!dateString) return "";
    
    try {
      const date = formatDateToDDMMYYYY(dateString);
      const sessionText = session === 'morning' ? 'Matin' : 
                         session === 'afternoon' ? 'Après-midi' : 
                         session || '';
      return sessionText ? `${date} ${sessionText}` : date;
    } catch (error) {
      console.error("Erreur lors du formatage de la date de début/fin:", error);
      return dateString;
    }
  };

  // Gestionnaires de pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  // Calculer les lignes à afficher pour la page courante
  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCloseRequestSummary = (event, reason) => {
    if (reason === "backdropClick") {
      return false;
    }
    setOpenDialogRequestSummary(false);
    setOpenDialog(false);
  };

  const handleCloseRequestSummaryMission = (event) => {
    setOpenDialogRequestSummaryMission(false);
    setOpenDialog(false);
  };

  const validateFile = (row) => () => {
    setRequestSummaryId(row);
   
    if (row.libelle_module_type === "mission") {
      setOpenDialogRequestSummaryMission(true);
    } else {
      setOpenDialogRequestSummary(true);
      setOpenDialog(true);
    }
  };

  function getFullName(row) {
    return `${row.submitter_person_first_name || ""} ${row.submitter_person_last_name || ""}`;
  }

  // Composant de carte mobile
  const MobileCard = ({ row }) => {
    const isOldRow = isOlderThanTwoWeeks(row.submission_time);
    const submissionDate = row.formatted_submission_date || formatDateToDDMMYYYY(row.submission_time);

    return (
      <Box className={`${classes.mobileCard} ${isOldRow ? classes.mobileCardOld : ''}`}>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Demandeur</Typography>
          <Typography className={classes.mobileCardValue}>{getFullName(row)}</Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Date création</Typography>
          <Typography className={classes.mobileCardValue}>
            {submissionDate}
            {isOldRow && (
              <Typography variant="caption" display="block" color="error" style={{ fontSize: '0.6rem' }}>
                (Ancienne)
              </Typography>
            )}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Type</Typography>
          <Chip 
            label={row.libelle_module_type || 'N/A'} 
            size="small" 
            className={classes.mobileCardChip}
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Départ</Typography>
          <Typography className={classes.mobileCardValue}>
            {formatDate(row.begin_time, row.begin_time_session)}
          </Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Retour</Typography>
          <Typography className={classes.mobileCardValue}>
            {formatDate(row.end_time, row.end_time_session)}
          </Typography>
        </Box>
        <Box className={classes.mobileCardActions}>
          {row.libelle_module_type === "Rapport de mission" ? (
            <Button
              onClick={() => navigate('/mission_report/' + row.file_validation_request_id)}
              title="Passer à la validation"
              className={classes.mobileActionButton}
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<Visibility style={{ fontSize: '18px' }} />}
            >
              Voir
            </Button>
          ) : (
            <Button
              onClick={validateFile(row)}
              title="Passer à la validation"
              className={classes.mobileActionButton}
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<FindInPageTwoTone style={{ fontSize: '18px' }} />}
            >
              Valider
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <MainContainer {...headerData}>
      {/* Dialogues */}
      {openDialogRequestSummaryMission && (
        <RequestSummaryMission
          requestSummaryId={requestSummaryId}
          openDialogRequestSummary={openDialogRequestSummaryMission}
          handleCloseRequestSummary={handleCloseRequestSummaryMission}
        />
      )}
      {openDialog && (
        <RequestSummary
          requestSummaryId={requestSummaryId}
          openDialogRequestSummary={openDialogRequestSummary}
          handleCloseRequestSummary={handleCloseRequestSummary}
          isRhConnected={isRhConnected}
        />
      )}

      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

      <Grid container className={classes.container} spacing={0}>
        <Grid item xs={12} className={classes.gridItem}>
          <Paper className={classes.paper} variant="outlined">
            {/* Statistiques cliquables */}
            <Box className={classes.statsContainer}>
              {STATS_DISPLAY.map((typeDef) => {
                const Icon = typeDef.icon;
                const isActive = typeFilter === typeDef.key;
                const count = typeDef.key === "all" ? rows.length : getCountForType(typeDef);
                return (
                  <Box
                    key={typeDef.key}
                    className={`${classes.statCard} ${isActive ? classes.statCardActive : ''}`}
                    onClick={handleStatClick(typeDef.key)}
                    role="button"
                    tabIndex={0}
                    title={`Filtrer sur : ${typeDef.label}`}
                  >
                    <Icon className={classes.statCardIcon} />
                    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography className={classes.statCardCount}>
                        {count}
                      </Typography>
                      <Typography className={classes.statCardLabel}>
                        {isSmallMobile ? typeDef.label.substring(0, 6) : typeDef.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            
            {/* Barre de filtres avec Grid - Version corrigée */}
            <Box className={classes.filterContainer}>
              <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
                {/* Colonne du select */}
                <Grid item xs={12} sm={5} md={4} lg={3} className={classes.filterGridItem}>
                  <FormControl variant="outlined" size="small" fullWidth className={classes.typeFilter}>
                    <InputLabel>
                      Type de demande
                    </InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      label="Type de demande"
                    >
                      <MenuItem value="all">Tous les types</MenuItem>
                      {REQUEST_TYPES.map((typeDef) => (
                        <MenuItem key={typeDef.key} value={typeDef.key}>
                          {typeDef.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Colonne des boutons */}
                <Grid item xs={12} sm={4} md={3} lg={2} className={classes.buttonGridItem}>
             
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FilterList style={{ fontSize: isMobile ? '18px' : '20px' }} />}
                      onClick={() => applyFilters(typeFilter)}
                      className={classes.filterButton}
                      size={isMobile ? "small" : "medium"}
                      fullWidth={isMobile}
                      style={{ flex: isMobile ? 1 : '0 1 auto' }}
                    >
                      {isMobile ? "Filtrer" : "Appliquer"}
                    </Button>
             
             
                </Grid>

           
                
                {/* Colonne des boutons */}
                <Grid item xs={12} sm={4} md={3} lg={2} className={classes.buttonGridItem}>
             
                     <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Clear style={{ fontSize: isMobile ? '18px' : '20px' }} />}
                      onClick={resetFilters}
                      className={classes.filterButton}
                      size={isMobile ? "small" : "medium"}
                      fullWidth={isMobile}
                      style={{ flex: isMobile ? 1 : '0 1 auto' }}
                    >
                      {isMobile ? "Réinit." : "Réinitialiser"}
                    </Button>
             
                </Grid>
                
                {/* Colonne de la légende */}
                <Grid item xs={12} sm={3} md={5} lg={7} className={classes.legendGridItem}>
                  <Box className={classes.resultsInfo}>    
                    <Box className={classes.legendRow}>
                      <Box display="flex" alignItems="center">
                        <Box 
                          className={classes.oldRowIndicator} 
                          height={isMobile ? "12px" : "16px"} 
                          width={isMobile ? "12px" : "16px"} 
                          mr={0.5} 
                        />
                        <Typography variant="caption" color="textSecondary" className={classes.mobileInfoText}>
                          {isMobile ? "Anciennes" : "Demandes de plus d'une semaine"}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" alignItems="center">
                        <Box 
                          className={classes.newRowIndicator} 
                          height={isMobile ? "12px" : "16px"} 
                          width={isMobile ? "12px" : "16px"} 
                          mr={0.5} 
                        />
                        <Typography variant="caption" color="textSecondary" className={classes.mobileInfoText}>
                          {isMobile ? "Nouvelles" : "Demandes nouvelles"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Tableau ou Cartes selon le device */}
            {isMobile ? (
              // Version mobile - Cartes avec 1 ligne par page
              <Paper elevation={1} className={classes.mobilePaper}>
                <Box 
                  ref={listRef}
                  className={classes.mobileCardContainer}
                  style={{ 
                    maxHeight: 'calc(100vh - 340px)',
                    minHeight: '300px'
                  }}
                >
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                      <MobileCard key={row.id} row={row} />
                    ))
                  ) : (
                    <Box className={classes.emptyState}>
                      <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                        Aucune demande trouvée
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            ) : (
              // Version desktop - Tableau
              <Paper elevation={3} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <TableContainer className={`${classes.tableContainer} ${classes.tableContainerDesktop}`}>
                  <Table stickyHeader className={classes.root}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date de création</TableCell>
                        <TableCell>Nom et prénom(s)</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Date de départ</TableCell>
                        <TableCell>Date de retour</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.length > 0 ? (
                        paginatedRows.map((row) => {
                          const submissionDate = row.formatted_submission_date || formatDateToDDMMYYYY(row.submission_time);
                          const isOldRow = isOlderThanTwoWeeks(row.submission_time);
                          
                          return (
                            <TableRow 
                              key={row.id} 
                              className={`${classes.tableRow} ${isOldRow ? 'row-old' : ''}`}
                            >
                              <TableCell>
                                {submissionDate}
                                {isOldRow && (
                                  <Typography variant="caption" display="block" color="error">
                                    (Ancienne demande)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{getFullName(row)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={row.libelle_module_type || 'N/A'} 
                                  size="small" 
                                  className={classes.statusChip}
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {formatDate(row.begin_time, row.begin_time_session)}
                              </TableCell>
                              <TableCell>
                                {formatDate(row.end_time, row.end_time_session)}
                              </TableCell>
                              <TableCell>
                                {row.libelle_module_type === "Rapport de mission" ? (
                                  <Button
                                    onClick={() => navigate('/mission_report/' + row.file_validation_request_id)}
                                    title="Passer à la validation"
                                    className={classes.actionButton}
                                    size="small"
                                  >
                                    <FindInPageTwoTone style={{ color: "black" }} />
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={validateFile(row)}
                                    title="Passer à la validation"
                                    className={classes.actionButton}
                                    size="small"
                                  >
                                    <FindInPageTwoTone style={{ color: "black" }} />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body1" color="textSecondary">
                              Aucune demande trouvée
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Pied de page avec pagination */}
            <div className={classes.footerRow}>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                {filteredRows.length} demande(s)
                {typeFilter !== "all" && ` - ${REQUEST_TYPES.find(t => t.key === typeFilter)?.label || typeFilter}`}
              </Typography>
              
              <TablePagination
                rowsPerPageOptions={isMobile ? [1] : [5, 10, 25, 50]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={isMobile ? "" : "Lignes par page :"}
                labelDisplayedRows={({ from, to, count }) =>
                  isMobile ? `${from}-${to}/${count}` : `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                }
                ActionsComponent={TablePaginationActions}
                className={classes.pagination}
              />
            </div>
          </Paper>
        </Grid>
      </Grid>
    </MainContainer>
  );
}